import { useState, useEffect } from "react";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { Palette, Trash2, Sparkles, Loader2, Eye, X, Copy, Plus, Pencil } from "lucide-react";
import ListToolbar from "@/components/ListToolbar";

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [platformTemplates, setPlatformTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("card");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewLoading, setPreviewLoading] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [adopting, setAdopting] = useState(null);
  const [editTpl, setEditTpl] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [aiInstructions, setAiInstructions] = useState("");


  const openEdit = (t) => {
    setEditTpl(t);
    setAiInstructions("");
    setEditForm({ name: t.name || "", description: t.description || "" });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/templates/${editTpl.id}`, { name: editForm.name, description: editForm.description });
      if (aiInstructions.trim()) {
        await api.post(`/templates/${editTpl.id}/design-html`, { instructions: aiInstructions });
        toast.success("Formato regenerado con IA. Revísalo con Vista Previa.");
      } else {
        toast.success("Template actualizado");
      }
      setEditTpl(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSavingEdit(false); }
  };

  const load = async () => {
    try {
      const [userRes, platRes] = await Promise.all([api.get("/templates"), api.get("/templates/platform")]);
      setTemplates(userRes.data);
      setPlatformTemplates(platRes.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      await api.post("/templates/generate-ai");
      toast.success("Template generado con IA");
      load();
    } catch { toast.error("Error generando"); }
    finally { setGenerating(false); }
  };

  const handleAdopt = async (t) => {
    setAdopting(t.id);
    try {
      await api.post(`/templates/adopt/${t.id}`);
      toast.success(`Template "${t.name}" adoptado`);
      setShowCreateModal(false);
      load();
    } catch { toast.error("Error adoptando template"); }
    finally { setAdopting(null); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm("¿Eliminar este template?")) return;
    await api.delete(`/templates/${t.id}`);
    toast.success("Eliminado");
    load();
  };

  const handlePreview = async (t) => {
    setPreviewLoading(t.id);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates/${t.id}/preview-pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("qf_token")}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Error generando vista previa");
      }
      const blob = await res.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewName(t.name);
    } catch (e) { toast.error(e.message || "Error generando vista previa"); }
    finally { setPreviewLoading(null); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewName("");
  };

  const filtered = templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || (t.description || "").toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black font-['Satoshi'] tracking-tight text-slate-900">Mis Templates</h1>
          <p className="text-slate-500 mt-1">{templates.length} creados</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-5 py-2.5 text-sm transition-all active:scale-95 flex items-center gap-2" data-testid="new-template-btn">
          <Plus size={16} /> Nuevo Template
        </button>
      </div>

      {templates.length > 0 && <ListToolbar search={search} onSearch={setSearch} viewMode={viewMode} onViewMode={setViewMode} />}

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Palette size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">{search ? "Sin resultados" : "Aún no tienes templates"}</p>
          {!search && <p className="text-sm text-slate-400 mt-1">Crea uno desde las propuestas de plataforma o genera uno con IA</p>}
          {!search && (
            <button onClick={() => setShowCreateModal(true)} className="mt-4 bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all" data-testid="create-first-template">
              Crear mi primer template
            </button>
          )}
        </div>
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all" data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex gap-1.5">
                  {Object.values(t.color_scheme || {}).map((c, i) => (
                    <div key={i} className="w-6 h-6 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: c }} />
                  ))}
                </div>
                <button onClick={() => handleDelete(t)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-tpl-${t.id}`}><Trash2 size={16} /></button>
              </div>
              <h3 className="font-bold text-slate-900">{t.name}</h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{t.description}</p>
              <div className="flex gap-2 mt-3">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{t.layout}</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{t.font_style}</span>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => handlePreview(t)} disabled={previewLoading === t.id}
                  className="flex-1 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 py-2 text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid={`preview-tpl-${t.id}`}>
                  {previewLoading === t.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  {previewLoading === t.id ? "Generando..." : "Vista Previa"}
                </button>
                <button onClick={() => openEdit(t)}
                  className="flex-1 rounded-lg bg-slate-900 text-white hover:bg-slate-800 py-2 text-sm font-medium transition-all flex items-center justify-center gap-2"
                  data-testid={`edit-tpl-${t.id}`}>
                  <Pencil size={14} /> Editar
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Colores</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Nombre</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Descripción</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Layout</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(t => (
                <tr key={t.id} className="hover:bg-slate-50/50" data-testid={`template-row-${t.id}`}>
                  <td className="px-5 py-3"><div className="flex gap-1">{Object.values(t.color_scheme || {}).map((c, i) => <div key={i} className="w-4 h-4 rounded-full" style={{ backgroundColor: c }} />)}</div></td>
                  <td className="px-5 py-3 font-medium text-slate-900">{t.name}</td>
                  <td className="px-5 py-3 text-slate-500 max-w-xs truncate">{t.description}</td>
                  <td className="px-5 py-3 text-slate-500">{t.layout}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handlePreview(t)} disabled={previewLoading === t.id} className="text-blue-500 hover:text-blue-700 p-1" data-testid={`preview-row-${t.id}`}>
                        {previewLoading === t.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                      </button>
                      <button onClick={() => openEdit(t)} className="text-slate-500 hover:text-slate-900 p-1" data-testid={`edit-row-${t.id}`}><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(t)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-template-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold font-['Satoshi'] text-slate-900">Crear Nuevo Template</h2>
                <p className="text-xs text-slate-500 mt-0.5">Elige una base de plataforma o genera uno con IA</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* AI Option */}
              <button onClick={() => { setShowCreateModal(false); handleGenerateAI(); }} disabled={generating}
                className="w-full mb-6 p-5 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 hover:bg-blue-50 hover:border-blue-300 transition-all text-left"
                data-testid="ai-generate-option">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Sparkles size={20} className="text-blue-600" /></div>
                  <div>
                    <h3 className="font-bold text-slate-900">Generar con IA</h3>
                    <p className="text-sm text-slate-500">La IA creará un diseño único basado en tu empresa</p>
                  </div>
                </div>
              </button>

              {/* Platform Templates */}
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Propuestas de la plataforma</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {platformTemplates.map(t => (
                  <div key={t.id} className="p-4 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-md transition-all" data-testid={`platform-tpl-${t.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex gap-1.5">
                        {Object.values(t.color_scheme || {}).map((c, i) => (
                          <div key={i} className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">{t.layout}</span>
                    </div>
                    <h4 className="font-bold text-slate-900 text-sm">{t.name}</h4>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => handlePreview(t)} disabled={previewLoading === t.id}
                        className="flex-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 py-1.5 text-xs font-medium transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                        data-testid={`preview-platform-${t.id}`}>
                        {previewLoading === t.id ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                        Vista Previa
                      </button>
                      <button onClick={() => handleAdopt(t)} disabled={adopting === t.id}
                        className="flex-1 rounded-lg bg-slate-900 text-white hover:bg-slate-800 py-1.5 text-xs font-medium transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                        data-testid={`adopt-platform-${t.id}`}>
                        {adopting === t.id ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />}
                        {adopting === t.id ? "Adoptando..." : "Usar este"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {editTpl && editForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-template-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold font-['Satoshi'] text-slate-900">Editar Template</h2>
              <button onClick={() => setEditTpl(null)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-edit-modal"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Nombre</label>
                <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-tpl-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Descripción</label>
                <textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={2}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-tpl-description" />
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                <label className="text-xs font-bold text-blue-800 flex items-center gap-1.5 mb-2"><Sparkles size={13} /> ¿Qué quieres cambiar del formato?</label>
                <textarea value={aiInstructions} onChange={e => setAiInstructions(e.target.value)} rows={4}
                  placeholder='Escríbelo en tus palabras. Ej: "Quiero que el logo aparezca en la primera hoja, al medio antes del título" o "quita la sección Sobre nosotros y agrega condiciones de pago"'
                  className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="ai-instructions-input" />
                <p className="text-[11px] text-slate-500 mt-2">La IA parte del diseño actual de este template y aplica exactamente lo que pidas (tarda ~30 seg). Luego revísalo con "Vista Previa". Si lo dejas vacío, solo se guardan nombre y descripción.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
              <button onClick={() => setEditTpl(null)} className="px-5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="cancel-edit-tpl">Cancelar</button>
              <button onClick={saveEdit} disabled={savingEdit || !editForm.name}
                className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="save-edit-tpl">
                {savingEdit && <Loader2 size={14} className="animate-spin" />}
                {savingEdit ? "Generando formato..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="template-preview-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold font-['Satoshi'] text-slate-900">Vista Previa: {previewName}</h2>
                <p className="text-xs text-slate-500 mt-0.5">PDF de ejemplo con datos ficticios</p>
              </div>
              <button onClick={closePreview} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="close-preview-modal"><X size={20} /></button>
            </div>
            <div className="flex-1 p-4 bg-slate-100 overflow-hidden rounded-b-2xl">
              <iframe src={previewUrl} className="w-full h-full rounded-lg border border-slate-200 bg-white" title="Template Preview" data-testid="template-preview-iframe" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
